import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { onAuthStateChanged } from "firebase/auth";
import { MethodologyEditDialog } from "./MethodologyEditDialog";
import { SubMethodologyEditDialog } from "./SubMethodologyEditDialog";
import { auth, methodologyService, Methodology, SubMethodology, SubMethodologyContent, DEFAULT_METHODOLOGIES } from "../../lib/firebase";

const ADMIN_EMAIL = "admin@rawpa.com";

// Helper function to count key points and tools across all content entries of a sub-methodology
const getSubMethodologyContentCounts = async (methodologyId: string, subMethodologyId: string): Promise<{keyPoints: number, tools: number}> => {
  try {
    const entries = await methodologyService.getAllSubMethodologyEntries(methodologyId, subMethodologyId);
    
    // Count all key points and tools in all entries
    let totalKeyPoints = 0;
    let totalTools = 0;
    
    entries.forEach(entry => {
      if (entry.keyPoints && Array.isArray(entry.keyPoints)) {
        totalKeyPoints += entry.keyPoints.length;
      }
      if (entry.tools && Array.isArray(entry.tools)) {
        totalTools += entry.tools.length;
      }
    });
    
    return { keyPoints: totalKeyPoints, tools: totalTools };
  } catch (error) {
    console.error("Error counting content items:", error);
    // Fall back to 0 if there's an error
    return { keyPoints: 0, tools: 0 };
  }
};

export const AdminMethodologies = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [methodologies, setMethodologies] = useState<Methodology[]>([]);
  const [editingMethodology, setEditingMethodology] = useState<Methodology | null>(null);
  const [editingSubMethodology, setEditingSubMethodology] = useState<{
    methodologyId: string;
    subMethodology: SubMethodology | null;
  } | null>(null);
  const [editingSubMethodologyContent, setEditingSubMethodologyContent] = useState<{
    methodologyId: string;
    subMethodologyId: string;
    content?: SubMethodologyContent;
    title?: string;
  } | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isCreatingNew, setIsCreatingNew] = useState(false); // Flag for creating new methodology
  const [contentEditDialogOpen, setContentEditDialogOpen] = useState(false);

  // Key points counts for each sub-methodology
  const [subMethodologyKeyPoints, setSubMethodologyKeyPoints] = useState<Record<string, number>>({});

  // Check authentication
  useEffect(() => {
    if (!auth) {
      setError("Authentication service unavailable");
      setMethodologies(DEFAULT_METHODOLOGIES);
      setLoading(false);
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
        return;
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load methodologies
  useEffect(() => {
    console.log("Setting up methodologies subscription in AdminMethodologies");
    setLoading(true);
    
    const unsubscribe = methodologyService.subscribeToMethodologies((updatedMethodologies) => {
      console.log("Admin received updated methodologies:", updatedMethodologies);
      // Show both straight-up and pathway methodologies
      const filtered = updatedMethodologies.filter(methodology => 
        methodology.id === 'straight-up' || methodology.id === 'pathway'
      );
      console.log("Filtered to straight-up and pathway methodologies:", filtered);
      setMethodologies(filtered);
      setLoading(false);
    });

    return () => {
      console.log("Cleaning up methodologies subscription in AdminMethodologies");
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  // Load key points counts for all sub-methodologies
  useEffect(() => {
    const loadKeyPoints = async () => {
      try {
        const keyPointsData: Record<string, number> = {};
        
        // Create an array of promises for all sub-methodologies across all methodologies
        const countPromises: Promise<{methodologyId: string, subMethodologyId: string, count: number}>[] = [];
        
        methodologies.forEach(methodology => {
          if (methodology.subMethodologies && methodology.subMethodologies.length > 0) {
            methodology.subMethodologies.forEach(subMethodology => {
              const promise = getSubMethodologyContentCounts(methodology.id, subMethodology.id)
                .then(count => ({
                  methodologyId: methodology.id,
                  subMethodologyId: subMethodology.id,
                  count: count.keyPoints + count.tools
                }));
              countPromises.push(promise);
            });
          }
        });
        
        // Wait for all counts to be fetched
        const results = await Promise.all(countPromises);
        
        // Build the results object with composite keys
        results.forEach(result => {
          const key = `${result.methodologyId}-${result.subMethodologyId}`;
          keyPointsData[key] = result.count;
        });
        
        setSubMethodologyKeyPoints(keyPointsData);
        
      } catch (error) {
        console.error("Error loading key points:", error);
        setError("Failed to load key points. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    
    loadKeyPoints();
  }, [methodologies]);

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/aAyuiOcvMq');
      } else {
        setError("Authentication service unavailable");
      }
    } catch (error) {
      console.error('Error signing out:', error);
      setError("Failed to sign out. Please try again.");
    }
  };

  // Handle creating a new methodology
  const handleCreateMethodology = () => {
    setIsCreatingNew(true);
    setEditingMethodology({
      id: '',
      title: '',
      description: '',
      subMethodologies: []
    });
    setEditDialogOpen(true);
  };

  const handleSaveMethodology = async (data: { title: string; description: string; keywords?: string[]; type?: 'straight-up' | 'pathway' }) => {
    setError(null); // Clear previous errors
    
    try {
      console.log("Saving methodology with data:", data);
      
      if (isCreatingNew) {
        // Prevent duplicate pathway or straight-up methodologies
        if (data.type === 'pathway' && methodologies.some(m => m.id === 'pathway')) {
          setError('A Pathway Methodology already exists. Only one is allowed.');
          return;
        }
        if (data.type === 'straight-up' && methodologies.some(m => m.id === 'straight-up')) {
          setError('A Straight-up Methodology already exists. Only one is allowed.');
          return;
        }
        // Set the id based on type
        const id = data.type === 'pathway' ? 'pathway' : 'straight-up';
        // Directly save the methodology with the correct id
        const success = await methodologyService.saveMethodology({
          id,
          title: data.title,
          description: data.description,
          keywords: data.keywords || [],
          subMethodologies: []
        });
        if (!success) {
          setError("Failed to create new methodology. Please try again.");
          return;
        }
        console.log(`Created new methodology with ID: ${id}`);
      } else if (editingMethodology) {
        // Handle updating an existing methodology
        const updatedMethodology = {
          ...editingMethodology,
          title: data.title,
          description: data.description,
          keywords: data.keywords || []
        };
        
        console.log("Saving methodology update:", updatedMethodology);
        const success = await methodologyService.saveMethodology(updatedMethodology);
        
        if (!success) {
          setError("Failed to save methodology. Changes may not persist.");
          
          // Update local state as fallback
          setMethodologies(prev => 
            prev.map(m => m.id === updatedMethodology.id ? updatedMethodology : m)
          );
        }
      }
    } catch (err) {
      console.error("Error saving methodology:", err);
      setError("An unexpected error occurred while saving. Please try again.");
    } finally {
      setEditingMethodology(null);
      setEditDialogOpen(false);
      setIsCreatingNew(false);
    }
  };

  const handleSaveSubMethodology = async (data: { title: string; description: string; keywords?: string[] }) => {
    setError(null); // Clear previous errors
    
    try {
      if (!editingSubMethodology) return;
      
      const parentMethodology = methodologies.find(m => m.id === editingSubMethodology.methodologyId);
      if (!parentMethodology) {
        setError("Parent methodology not found. Please refresh the page and try again.");
        return;
      }

      console.group('Sub-methodology update');
      console.log('Parent methodology ID:', parentMethodology.id);
      console.log('Parent methodology before update:', parentMethodology);
      console.log('Form data being saved:', data);
      
      if (editingSubMethodology.subMethodology) {
        // Update existing sub-methodology
        const subMethodologyId = editingSubMethodology.subMethodology.id;
        const updates = {
          title: data.title,
          description: data.description,
          keywords: data.keywords || []
        };
        
        console.log(`Updating sub-methodology ${subMethodologyId}:`, updates);
        
        // Find and update the sub-methodology in the parent
        const updatedSubMethodologies = parentMethodology.subMethodologies.map(sub => {
          if (sub.id === subMethodologyId) {
            return {
              ...sub,
              ...updates
            };
          }
          return sub;
        });
        
        // Create the updated methodology object
        const updatedMethodology = {
          ...parentMethodology,
          subMethodologies: updatedSubMethodologies
        };
        
        console.log('Updated methodology:', updatedMethodology);
        
        // Save the updated methodology
        const success = await methodologyService.saveMethodology(updatedMethodology);
        
        if (!success) {
          setError("Failed to save sub-methodology. Changes may not persist.");
          
          // Update local state as fallback
          setMethodologies(prev => 
            prev.map(m => m.id === updatedMethodology.id ? updatedMethodology : m)
          );
        }
      } else {
        // Create new sub-methodology
        const initialContent = {
          name: data.title,
          definition: '',
          keyPoints: [],
          commonBugs: [],
          pentestingTips: [],
          tools: [],
          extra: '',
          completedItems: []
        };
        
        // Generate a single consistent ID for the entry
        const entryId = `entry-${Date.now()}`;
        
        const newSubMethodology = {
          id: `sub-${Date.now()}`,
          title: data.title,
          description: data.description,
          keywords: data.keywords || [],
          detailedContent: initialContent,
          // Add multiContent structure to ensure compatibility with both access methods
          multiContent: {
            entries: [{
              id: entryId,
              ...initialContent
            }],
            activeEntryId: entryId
          }
        };
        
        console.log('Creating new sub-methodology:', newSubMethodology);
        
        // Add the new sub-methodology to the parent
        const updatedMethodology = { 
          ...parentMethodology,
          subMethodologies: [...parentMethodology.subMethodologies, newSubMethodology]
        };

        console.log('Updated methodology with new sub-methodology:', updatedMethodology);
        
        // Save the updated methodology
        const success = await methodologyService.saveMethodology(updatedMethodology);
        
        if (!success) {
          setError("Failed to create sub-methodology. Changes may not persist.");
          
          // Update local state as fallback
          setMethodologies(prev => 
            prev.map(m => m.id === updatedMethodology.id ? updatedMethodology : m)
          );
        }
      }
      
      console.groupEnd();
    } catch (err) {
      console.error("Error saving sub-methodology:", err);
      setError("An unexpected error occurred while saving. Please try again.");
    } finally {
      setEditingSubMethodology(null);
      setEditDialogOpen(false);
    }
  };

  // Handle saving sub-methodology content
  const handleSaveSubMethodologyContent = async (data: SubMethodologyContent) => {
    setError(null); // Clear previous errors
    
    try {
      if (!editingSubMethodologyContent) return;
      
      console.group('Sub-methodology content update');
      console.log(`Updating content for sub-methodology ${editingSubMethodologyContent.subMethodologyId} in methodology ${editingSubMethodologyContent.methodologyId}`);
      console.log('Content data:', data);
      
      const success = await methodologyService.updateSubMethodologyContent(
        editingSubMethodologyContent.methodologyId,
        editingSubMethodologyContent.subMethodologyId,
        data
      );
      
      if (success) {
        console.log('Sub-methodology content updated successfully');
      } else {
        setError("Failed to update sub-methodology content. Please try again.");
      }
      console.groupEnd();
    } catch (err) {
      console.error("Error saving sub-methodology content:", err);
      setError("An unexpected error occurred. Please try again.");
      console.groupEnd();
    } finally {
      setEditingSubMethodologyContent(null);
      setContentEditDialogOpen(false);
    }
  };

  const handleDeleteMethodology = async (methodologyId: string) => {
    if (window.confirm("Are you sure you want to delete this methodology? This action cannot be undone.")) {
      setError(null);
      
      try {
        console.log(`Deleting methodology: ${methodologyId}`);
        const success = await methodologyService.deleteMethodology(methodologyId);
        
        if (!success) {
          setError("Failed to delete methodology. Please try again.");
          
          // Update local state as fallback
          setMethodologies(prev => prev.filter(m => m.id !== methodologyId));
        }
      } catch (err) {
        console.error("Error deleting methodology:", err);
        setError("An unexpected error occurred while deleting. Please try again.");
      }
    }
  };

  const handleDeleteSubMethodology = async (methodologyId: string, subMethodologyId: string) => {
    if (window.confirm("Are you sure you want to delete this sub-methodology? This action cannot be undone.")) {
      setError(null);
      
      try {
        const methodology = methodologies.find(m => m.id === methodologyId);
        if (!methodology) {
          setError("Methodology not found. Please refresh the page.");
          return;
        }

        const updatedMethodology = {
          ...methodology,
          subMethodologies: methodology.subMethodologies.filter(sm => sm.id !== subMethodologyId)
        };

        console.log(`Deleting sub-methodology ${subMethodologyId} from methodology ${methodologyId}`);
        const success = await methodologyService.saveMethodology(updatedMethodology);
        
        if (!success) {
          setError("Failed to delete sub-methodology. Please try again.");
          
          // Update local state as fallback
          setMethodologies(prev => 
            prev.map(m => m.id === methodologyId ? updatedMethodology : m)
          );
        }
      } catch (err) {
        console.error("Error deleting sub-methodology:", err);
        setError("An unexpected error occurred while deleting. Please try again.");
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/aAyuiOcvMq/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div 
              onClick={handleSignOut}
              className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
            >
              <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
            </div>
          </div>
        </div>
      </div>

      {/* Edit Dialogs */}
      <MethodologyEditDialog
        open={editDialogOpen && editingMethodology !== null}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingMethodology(null);
          setIsCreatingNew(false);
        }}
        onSave={handleSaveMethodology}
        title={editingMethodology?.title || ''}
        description={editingMethodology?.description || ''}
        keywords={editingMethodology?.keywords || []}
        isNew={isCreatingNew}
      />

      <SubMethodologyEditDialog
        open={editDialogOpen && editingSubMethodology !== null}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingSubMethodology(null);
        }}
        onSave={handleSaveSubMethodology}
        title={editingSubMethodology?.subMethodology?.title || ''}
        description={editingSubMethodology?.subMethodology?.description || ''}
        keywords={editingSubMethodology?.subMethodology?.keywords || []}
        isNew={editingSubMethodology?.subMethodology === null}
      />

      {/* Dialog no longer used - replaced by SubMethodologyContentPage */}
      {/* <SubMethodologyEditDialog
        isOpen={contentEditDialogOpen && editingSubMethodologyContent !== null}
        onClose={() => {
          setContentEditDialogOpen(false);
          setEditingSubMethodologyContent(null);
        }}
        onSave={handleSaveSubMethodologyContent}
        content={editingSubMethodologyContent?.content}
        title={editingSubMethodologyContent?.title}
      /> */}

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px]">
        {error && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}
        
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <button 
                onClick={() => navigate('/aAyuiOcvMq/dashboard')}
                className="text-black opacity-60 hover:opacity-100 transition-opacity"
              >
                ← Back to Dashboard
              </button>
              
              <Button
                className="h-9 px-4 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                onClick={handleCreateMethodology}
              >
                Create New Methodology
              </Button>
            </div>
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Manage Methodologies
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Edit and organize your straight-up and pathway assessment methodologies
            </p>
          </div>

          {methodologies.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No methodologies found. Create your first methodology by clicking the button above.
            </div>
          ) : (
            <div className="space-y-4">
              {methodologies.map((methodology) => (
                <div 
                  key={methodology.id}
                  className="bg-white p-6 rounded-[20px] border border-[#E7E7E7] shadow-sm"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black">
                        {methodology.title}
                      </h3>
                      {methodology.description && (
                        <p className="text-sm text-black opacity-60">
                          {methodology.description}
                        </p>
                      )}
                      {methodology.updatedAt && (
                        <p className="text-xs text-gray-500 mt-1">
                          Last updated: {new Date(methodology.updatedAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        className="h-8 px-3 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
                        onClick={() => {
                          setEditingMethodology(methodology);
                          setIsCreatingNew(false);
                          setEditDialogOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        className="h-8 px-3 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                        onClick={() => handleDeleteMethodology(methodology.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>

                  {/* Sub-methodologies */}
                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <h4 className="font-['Space_Grotesk'] font-medium text-sm text-black">
                        Sub-methodologies
                      </h4>
                      <Button
                        className="h-7 px-2 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors text-xs"
                        onClick={() => {
                          setEditingSubMethodology({ methodologyId: methodology.id, subMethodology: null });
                          setEditDialogOpen(true);
                        }}
                      >
                        Add New
                      </Button>
                    </div>
                    
                    {methodology.subMethodologies.length === 0 ? (
                      <div className="text-center py-4 text-gray-400 text-sm bg-gray-50 rounded-lg">
                        No sub-methodologies yet. Add one using the button above.
                      </div>
                    ) : (
                      methodology.subMethodologies.map((sub) => (
                        <div 
                          key={sub.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div>
                            <h5 className="font-['Space_Grotesk'] font-medium text-sm text-black">
                              {sub.title}
                            </h5>
                            {sub.description && (
                              <p className="text-xs text-black opacity-60">
                                {sub.description}
                              </p>
                            )}
                            <div className="mt-1 flex gap-1 flex-wrap">
                              {/* Use the combined key points count if available */}
                              {subMethodologyKeyPoints[`${methodology.id}-${sub.id}`] > 0 ? (
                                <span className="inline-block px-2 py-0.5 bg-[#daed6e40] text-black text-xs rounded">
                                  {subMethodologyKeyPoints[`${methodology.id}-${sub.id}`]} key points
                                </span>
                              ) : sub.detailedContent?.keyPoints && sub.detailedContent.keyPoints.length > 0 ? (
                                <span className="inline-block px-2 py-0.5 bg-[#daed6e40] text-black text-xs rounded">
                                  {sub.detailedContent.keyPoints.length} key points
                                </span>
                              ) : null}
                              
                              {sub.detailedContent?.tools && sub.detailedContent.tools.length > 0 && (
                                <span className="inline-block px-2 py-0.5 bg-[#daed6e40] text-black text-xs rounded">
                                  {sub.detailedContent.tools.length} tools
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              className="text-xs text-black opacity-60 hover:opacity-100"
                              onClick={() => {
                                setEditingSubMethodology({ methodologyId: methodology.id, subMethodology: sub });
                                setEditDialogOpen(true);
                              }}
                            >
                              Edit
                            </button>
                            <button
                              className="text-xs text-black opacity-60 hover:opacity-100"
                              onClick={() => navigate(`/aAyuiOcvMq/methodology/${methodology.id}/sub-methodology/${sub.id}/content`)}
                            >
                              Content
                            </button>
                            <button
                              className="text-xs text-red-600 opacity-60 hover:opacity-100"
                              onClick={() => handleDeleteSubMethodology(methodology.id, sub.id)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};